/* global Modernizr */

import { Controller } from 'stimulus';
import { Loader } from 'google-maps';
import MarkerClusterer from '@googlemaps/markerclustererplus';
import Sticky from 'sticky-js';
import throttle from '@/helpers/throttle';
import mapStyle from '@/helpers/mapStyle';

let google = null;
const HIDDEN_CLASS = 'is-hidden';
const isMobileNavigation = () => Modernizr.mq('(max-width: 991px)');

class MapController extends Controller {
  static getApiKey() {
    return window.app.api.keys.GOOGLE_MAPS;
  }

  static getIcon(name) {
    return `<svg class='icon icon-${name}' aria-hidden="true"><use xlink:href='#icon-${name}'></use></svg>`;
  }

  connect() {
    this.onResize();
    this.throttledScroll = throttle(this.checkVisibility.bind(this), 150);

    if (!this.hasMapTarget) return;

    this.locations = [];
    this.markers = [];

    const loader = new Loader(MapController.getApiKey());

    loader.load().then((googleInstance) => {
      google = googleInstance;
      this.createMap(googleInstance);
    });

    if (!isMobileNavigation()) {
      this.sticky = new Sticky('.map', {
        marginTop: 47,
        stickyClass: 'is-stuck',
      });
    }
  }

  defaultOptions() {
    let options = {
      center: {
        lat: parseFloat(this.mapTarget.getAttribute('data-latitude')) || 54.453519,
        lng: parseFloat(this.mapTarget.getAttribute('data-longitude')) || -0.068228,
      },
      zoom: 5,
      disableDefaultUI: true,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.TOP_RIGHT,
      },
      styles: mapStyle,
    };

    if (typeof this.mapTarget.getAttribute('data-disable-all-controls') !== 'undefined'
      && this.mapTarget.getAttribute('data-disable-all-controls') === 'true') {
      const extraOptions = {
        zoomControl: false,
        draggable: false,
        scrollwheel: false,
        disableDoubleClickZoom: true,
      };

      options = { ...options, ...extraOptions };
    }

    return options;
  }

  createMap(googleInstance) {
    this.map = new googleInstance.maps.Map(this.mapTarget, this.defaultOptions());

    if (typeof this.mapTarget.dataset.locations !== 'undefined') {
      this.locations = JSON.parse(this.mapTarget.dataset.locations);
      this.addMarkers();
      this.addClusters();
    }
  }

  addClusters() {
    this.clusters = new MarkerClusterer(this.map, this.markers, {
      maxZoom: 8,
      styles: [
        {
          url: '/front_assets/shared/pin-empty.svg',
          width: 34,
          height: 34,
          className: 'map__cluster',
        },
      ],
    });
  }

  addMarkers() {
    this.locations.forEach((location) => {
      const marker = this.createMarkerAtCoordinates(location);

      this.markers.push(marker);

      marker.addListener('click', () => {
        this.activateMarker(marker);
        this.openTooltip(location);
      });
    });
  }

  activateMarker(marker) {
    this.deactivateMarkers();

    const icon = {
      url: '/front_assets/shared/pin-active.svg',
      size: google.maps.Size(34, 34),
    };

    marker.setIcon(icon);
  }

  deactivateMarkers() {
    const icon = {
      url: '/front_assets/shared/pin.svg',
      size: google.maps.Size(34, 34),
    };

    this.markers.forEach((marker) => {
      marker.setIcon(icon);
    });
  }

  createMarkerAtCoordinates({ id, latitude, longitude }) {
    return new google.maps.Marker({
      id,
      map: this.map,
      position: {
        lat: parseFloat(latitude),
        lng: parseFloat(longitude),
      },
      icon: {
        url: '/front_assets/shared/pin.svg',
        size: google.maps.Size(34, 34),
      },
    });
  }

  openTooltip(location) {
    const item = document.querySelector(`[data-id="${location.id}"]`);

    if (!item) return;

    const { dataset } = item;
    const tooltip = this.createTooltip(dataset);

    this.mapTarget.appendChild(tooltip[0]);
  }

  closeTooltip() {
    this.tooltipTargets.forEach((tooltip) => tooltip.remove());
  }

  // eslint-disable-next-line class-methods-use-this
  createTooltip(data) {
    return $(`
      <div class="map__tooltip" data-target="map.tooltip">
        <button
          type="button"
          data-action="click->map#onTooltipCloseClick"
          class="map__tooltip-close btn btn--primary btn--rotate btn--icon-only"
        >
          ${MapController.getIcon('times')}
        </button>

        <div class="map__tooltip-image position-relative overflow-hidden">
          <img src="${data.image}" alt="${data.title}" class="img-fit absolute-center">
        </div>

        <div class="map__tooltip-body">
          <div class="map__tooltip-label">${data.label}</div>
          <a href="${data.url}" class="map__tooltip-title">
            ${data.title} ${MapController.getIcon('chevron-right')}
          </a>
        </div>
      </div>
    `);
  }

  toggleMap() {
    this.wrapperTarget.classList.toggle('is-open');
  }

  checkVisibility() {
    const top = window.scrollY;
    const isHit = (top + this.windowHeight) > this.containerOffset;

    if (isHit && !this.togglerTarget.classList.contains(HIDDEN_CLASS)) {
      this.togglerTarget.classList.add(HIDDEN_CLASS);
    } else if (!isHit && this.togglerTarget.classList.contains(HIDDEN_CLASS)) {
      this.togglerTarget.classList.remove(HIDDEN_CLASS);
    }
  }

  onTooltipCloseClick() {
    this.deactivateMarkers();
    this.closeTooltip();
  }

  onItemHover({ currentTarget }) {
    const id = parseInt(currentTarget.getAttribute('data-id'), 10);
    const correspondingMarker = this.markers.filter((marker) => marker.id === id);

    this.activateMarker(correspondingMarker[0]);
  }

  onTogglerClick(e) {
    if (!this.hasWrapperTarget) return;

    if (isMobileNavigation()) {
      e.preventDefault();
      this.toggleMap();
    }
  }

  onScroll() {
    this.throttledScroll();
  }

  onResize() {
    if (!this.hasContainerTarget) return;

    this.windowHeight = document.documentElement.clientHeight;
    this.containerOffset = this.containerTarget.offsetHeight;
  }
}

MapController.targets = [
  'map',
  'tooltip',
  'locations',
  'wrapper',
  'toggler',
  'container',
];

export default MapController;
